import { Navigate, NavLink, Outlet, Link, useLocation } from "react-router-dom";
import { CalendarDays, Inbox, Loader2, LogOut, Map, Ship, ExternalLink, Star, BarChart3, Link2, Users, Mail, Send, Plus, CreditCard, FileCheck } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { Logo } from "@/components/site/Header";
import ChangePassword from "@/pages/admin/ChangePassword";

const LINKS: { to: string; label: string; icon: React.ElementType; id: string; cashier?: boolean }[] = [
  { to: "/admin/ingresos", label: "Ingresos", icon: BarChart3, id: "admin-nav-dashboard" },
  { to: "/admin/bookings", label: "Reservas", icon: Inbox, id: "admin-nav-bookings" },
  { to: "/admin/yachts", label: "Yates", icon: Ship, id: "admin-nav-yachts" },
  { to: "/admin/calendar", label: "Calendario", icon: CalendarDays, id: "admin-nav-calendar" },
  { to: "/admin/tours", label: "Tours", icon: Map, id: "admin-nav-tours" },
  { to: "/admin/reviews", label: "Reseñas", icon: Star, id: "admin-nav-reviews" },
  { to: "/admin/links", label: "Links de pago", icon: Link2, id: "admin-nav-links", cashier: true },
  { to: "/admin/generar", label: "Generar link", icon: Plus, id: "admin-nav-generate", cashier: true },
  { to: "/admin/pagos", label: "Pagos", icon: CreditCard, id: "admin-nav-orders", cashier: true },
  { to: "/admin/confirmaciones", label: "Confirmaciones", icon: FileCheck, id: "admin-nav-confirmations", cashier: true },
  { to: "/admin/cajeros", label: "Cajeros", icon: Users, id: "admin-nav-cashiers" },
  { to: "/admin/correos", label: "Correos", icon: Mail, id: "admin-nav-emails", cashier: true },
  { to: "/admin/envios", label: "Envíos", icon: Send, id: "admin-nav-email-log" },
];

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const { pathname } = useLocation();
  if (user === null) return <div className="min-h-screen grid place-items-center"><Loader2 className="h-8 w-8 animate-spin text-[#00A8A0]" /></div>;
  if (user === false) return <Navigate to="/admin/login" replace />;
  const isCashier = user.role === "cashier";
  const CASHIER_PATHS = ["/admin/links", "/admin/correos", "/admin/generar", "/admin/pagos", "/admin/confirmaciones"];
  if (isCashier && !CASHIER_PATHS.includes(pathname)) return <Navigate to="/admin/links" replace />;
  const links = LINKS.filter((l) => !isCashier || l.cashier);
  const cls = ({ isActive }: { isActive: boolean }) =>
    `flex items-center gap-3 rounded-md px-3 py-2.5 text-sm whitespace-nowrap transition-colors ${isActive ? "bg-white/10 text-[#00D2C8]" : "text-white/70 hover:text-white hover:bg-white/5"}`;
  return (
    <div className="min-h-screen bg-slate-50 lg:flex">
      <aside className="bg-[#0B192C] lg:w-64 lg:min-h-screen lg:sticky lg:top-0 flex lg:flex-col">
        <div className="hidden lg:block p-6 border-b border-white/10"><Logo /></div>
        <nav className="flex lg:flex-col gap-1 p-3 overflow-x-auto flex-1">
          {links.map(({ to, label, icon: I, id }) => <NavLink key={to} to={to} className={cls} data-testid={id}><I className="h-4 w-4" />{label}</NavLink>)}
        </nav>
        <div className="hidden lg:block p-4 border-t border-white/10 space-y-2">
          <p className="text-xs text-white/50 truncate" data-testid="admin-user-email">{user.email}</p>
          <Link to="/" className="flex items-center gap-2 text-xs text-white/60 hover:text-white"><ExternalLink className="h-3.5 w-3.5" />Ver sitio</Link>
          <ChangePassword className="flex items-center gap-2 text-sm text-white/70 hover:text-white" />
          <button onClick={logout} data-testid="admin-logout-btn" className="flex items-center gap-2 text-sm text-white/70 hover:text-white"><LogOut className="h-4 w-4" />Cerrar sesión</button>
        </div>
        <div className="lg:hidden flex items-center gap-1 pr-2">
          <ChangePassword className="px-2 text-white/70 [&>svg]:h-5 [&>svg]:w-5 text-[0px]" />
          <button onClick={logout} data-testid="admin-logout-btn-mobile" aria-label="Cerrar sesión" className="px-2 text-white/70"><LogOut className="h-5 w-5" /></button>
        </div>
      </aside>
      <main className="flex-1 min-w-0 p-4 sm:p-8"><Outlet /></main>
    </div>
  );
}
